import { createApi } from '@reduxjs/toolkit/query/react';

import {
  RobotsEndpoints as Endpoints,
  type CheckKeywordOverlapsRequest,
  type CheckKeywordOverlapsResponse,
  type CreateRobotRequest,
  type CreateRobotResponse,
  type DeleteRobotRequest,
  type DeleteRobotResponse,
  type GetRobotForIdRequest,
  type GetRobotForIdResponse,
  type GetRobotLogsSearchResultRequest,
  type GetRobotLogsSearchResultResponse,
  type GetRobotsListForStageIdRequest,
  type GetRobotsListForStageIdResponse,
  type GetRobotsListTriggersRequest,
  type GetRobotsListTriggersResponse,
  type GetRobotsListTypesRequest,
  type GetRobotsListTypesResponse,
  type LogsMarkAsReadRequest,
  type LogsMarkAsReadResponse,
  type SearchRobotLogsRequest,
  type SearchRobotLogsResponse,
  type UpdateRobotRequest,
  type UpdateRobotResponse,
} from '@app/api/Robots/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { SALES_FUNNELS_STAGES_ROBOTS_CHUNK } from '@app/constants';

export const robotsApi = createApi({
  reducerPath: 'robotsApi',
  baseQuery: createApiBaseQuery(SALES_FUNNELS_STAGES_ROBOTS_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetRobotsListTypes]: query<
      GetRobotsListTypesResponse,
      GetRobotsListTypesRequest
    >({
      query: (params) => ({
        url: 'ListTypes',
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRobotsListTriggers]: query<
      GetRobotsListTriggersResponse,
      GetRobotsListTriggersRequest
    >({
      query: (params) => ({
        url: 'ListTriggers',
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRobotsListForStageId]: query<
      GetRobotsListForStageIdResponse,
      GetRobotsListForStageIdRequest
    >({
      query: ({ stageId, ...params }) => ({
        url: `List/${stageId}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRobotForId]: query<
      GetRobotForIdResponse,
      GetRobotForIdRequest
    >({
      query: ({ id, ...params }) => ({
        url: `Get/${id}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.DeleteRobot]: mutation<DeleteRobotResponse, DeleteRobotRequest>({
      query: ({ id, ...params }) => ({
        url: `Delete/${id}`,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.UpdateRobot]: mutation<UpdateRobotResponse, UpdateRobotRequest>({
      query: ({ id, data, ...params }) => ({
        url: `Update/${id}`,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
    [Endpoints.CreateRobot]: mutation<CreateRobotResponse, CreateRobotRequest>({
      query: ({ data, ...params }) => ({
        url: 'Create',
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SearchRobotLogs]: mutation<
      SearchRobotLogsResponse,
      SearchRobotLogsRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'SearchLogs',
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.LogsMarkAsRead]: mutation<
      LogsMarkAsReadResponse,
      LogsMarkAsReadRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'LogsMarkAsRead',
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetRobotLogsSearchResult]: query<
      GetRobotLogsSearchResultResponse,
      GetRobotLogsSearchResultRequest
    >({
      query: (params) => ({
        url: 'GetLogsSearchResult',
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CheckKeywordOverlaps]: mutation<
      CheckKeywordOverlapsResponse,
      CheckKeywordOverlapsRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'CheckKeywordOverlaps',
        method: Methods.POST,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useGetRobotsListTypesQuery,
  useLazyGetRobotsListTypesQuery,
  useGetRobotsListTriggersQuery,
  useLazyGetRobotsListTriggersQuery,
  useGetRobotsListForStageIdQuery,
  useLazyGetRobotsListForStageIdQuery,
  useGetRobotForIdQuery,
  useLazyGetRobotForIdQuery,
  useDeleteRobotMutation,
  useUpdateRobotMutation,
  useCreateRobotMutation,
  useSearchRobotLogsMutation,
  useLogsMarkAsReadMutation,
  useGetRobotLogsSearchResultQuery,
  useLazyGetRobotLogsSearchResultQuery,
  useCheckKeywordOverlapsMutation,
} = robotsApi;
